import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';
import CreateNewSchedule from 'src/sections/calendar/view/create-new-schedule';

// ----------------------------------------------------------------------

// Overview
const IndexPage = lazy(() => import('src/pages/dashboard'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
const OverviewCoursePage = lazy(() => import('src/pages/dashboard/course'));
// Order
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// Invoice
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// User
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// Blog
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// Job
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// Tour
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// File manager
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// App
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const CalendarClientPage = lazy(() => import('src/pages/dashboard/calendar-client'));
const EstablishmentPackagesServicesPage = lazy(
  () => import('src/pages/dashboard/establishment-packages-services')
);
const PortfolioPage = lazy(() => import('src/pages/dashboard/portfolio'));
const MySummaryPage = lazy(() => import('src/pages/dashboard/my-summary'));
const PlansPage = lazy(() => import('src/pages/dashboard/plans'));
const DoneServicesPage = lazy(() => import('src/pages/dashboard/done-services'));

const FinancialReportPage = lazy(() => import('src/pages/dashboard/financial-report'));
const FinancialHomePage = lazy(() => import('src/pages/dashboard/financial-home'));
const FinancialCashFlow = lazy(() => import('src/pages/dashboard/financial-cash-flow'));
const FinancialExpensePage = lazy(() => import('src/pages/dashboard/financial-expense'));

const HelpPage = lazy(() => import('src/pages/dashboard/help'));
const OpeningHourPage = lazy(() => import('src/pages/dashboard/opening_hour'));
const ManagePartnerProfessionalsPage = lazy(
  () => import('src/pages/dashboard/manage-partner-professionals')
);
const ClientsPage = lazy(() => import('src/pages/dashboard/clients'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// Test render page by role
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// Blank page
const ParamsPage = lazy(() => import('src/pages/dashboard/params'));
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
const ClientPage = lazy(() => import('src/pages/dashboard/client'));
const ProductPage = lazy(() => import('src/pages/dashboard/product'));
const NewProductPage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductReservationsPage = lazy(() => import('src/pages/dashboard/product/reservations'));
// My Account
const MyAccountPage = lazy(() => import('src/pages/dashboard/my-account/index'));

// ----------------------------------------------------------------------

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      { element: <EstablishmentPackagesServicesPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      { path: 'course', element: <OverviewCoursePage /> },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductPage />, index: true },
          { path: 'new', element: <NewProductPage /> },
          { path: 'list', element: <ProductListPage /> },
          { path: 'reservations', element: <ProductReservationsPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'establishment-packages-services', element: <EstablishmentPackagesServicesPage /> },
      { path: 'portfolio', element: <PortfolioPage /> },
      { path: 'clients', element: <ClientsPage /> },
      { path: 'my-summary', element: <MySummaryPage /> },
      { path: 'done-services', element: <DoneServicesPage /> },
      { path: 'financial-home/financial-report', element: <FinancialReportPage /> },
      { path: 'financial-home', element: <FinancialHomePage /> },
      { path: 'financial-home/cash-flow', element: <FinancialCashFlow /> },
      { path: 'financial-home/expense', element: <FinancialExpensePage /> },
      { path: 'help', element: <HelpPage /> },
      { path: 'opening_hours', element: <OpeningHourPage /> },
      { path: 'manage_partner_professionals', element: <ManagePartnerProfessionalsPage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'calendar-client', element: <CalendarClientPage /> },
      { path: 'calendar/new-schedule', element: <CreateNewSchedule /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'params', element: <ParamsPage /> },
      { path: 'blank', element: <BlankPage /> },
      { path: 'client', element: <ClientPage /> },
      { path: 'my-account', element: <MyAccountPage /> },
    ],
  },
];
